/** Motivo tab config for Sem Interesse and Pendente (aligned with backend config modules). */

import type { WorkspaceMotivoData } from '../api/cccApi';
import { readWorkspaceMotivoKeys } from '../utils/motivoKeys';
import { normalizeWorkspaceSubstage } from './recuperacaoWorkspace';

export type WorkspaceMotivoVariant = 'sem_interesse' | 'pendente';

export type WorkspaceMotivoOption = {
  key: string;
  label: string;
};

export type WorkspaceMotivoCategory = {
  id: string;
  label: string;
  options: WorkspaceMotivoOption[];
};

export const MOTIVO_WARNING_TEXT =
  'Não tem nenhuma opção selecionada ou não deixou nenhum comentário sobre este caso';
export const PENDENTE_MOTIVO_WARNING_TEXT = 'Indique o Motivo para estar Pendente';
export const SEM_INTERESSE_MOTIVO_WARNING_TEXT = 'Indique o Motivo para estar Sem Interesse';

export const SEM_INTERESSE_SUBSTAGE = 'em-tratamento-sem-interesse';
export const PROSPECCAO_SEM_INTERESSE_SUBSTAGE = 'por-contactar-sem-interesse';
export const PENDENTE_SUBSTAGE = 'em-tratamento-pendente';

const SEM_INTERESSE_SUBSTAGES = new Set([
  SEM_INTERESSE_SUBSTAGE,
  PROSPECCAO_SEM_INTERESSE_SUBSTAGE,
]);

const SEM_INTERESSE_CATEGORIES: WorkspaceMotivoCategory[] = [
  {
    id: 'proposta_comercial',
    label: 'Proposta / Comercial',
    options: [
      { key: 'preco-elevado', label: 'Preço elevado' },
      { key: 'proposta-nao-adequada', label: 'Proposta não adequada às necessidades' },
      { key: 'nao-aceita-fidelizacao-edp', label: 'Não Aceita Fidelização EDP' },
    ],
  },
  {
    id: 'decisao_empresa',
    label: 'Decisão / Empresa',
    options: [
      { key: 'sem-poder-decisao', label: 'Sem poder de decisão / Responsável indisponível' },
      { key: 'projeto-adiado', label: 'Projeto adiado / sem prioridade' },
    ],
  },
  {
    id: 'contacto',
    label: 'Contacto',
    options: [
      { key: 'nao-atende', label: 'Não atende / não responde' },
      { key: 'recusa-contacto', label: 'Recusa contacto' },
    ],
  },
  {
    id: 'outro',
    label: 'Outro',
    options: [{ key: 'outro-motivo', label: 'Outro (descrever no campo abaixo)' }],
  },
];

const PENDENTE_CATEGORIES: WorkspaceMotivoCategory[] = [
  {
    id: 'aguardar_cliente',
    label: 'Aguardar cliente',
    options: [
      { key: 'retorno-cliente', label: 'A aguardar retorno do cliente' },
      { key: 'ligar-mais-tarde', label: 'Cliente pediu para contactar mais tarde' },
      { key: 'reuniao-agendada', label: 'Reunião agendada — aguardar confirmação' },
    ],
  },
  {
    id: 'decisao',
    label: 'Decisão',
    options: [
      { key: 'cliente-analisa', label: 'Cliente a analisar proposta / informação' },
      { key: 'decisor-indisponivel', label: 'Pendente decisão do Responsável' },
    ],
  },
  {
    id: 'outro',
    label: 'Outro',
    options: [{ key: 'outro-pendente', label: 'Outro (descrever no campo abaixo)' }],
  },
];

export const WORKSPACE_MOTIVO_CONFIG: Record<
  WorkspaceMotivoVariant,
  { substage: string; categories: WorkspaceMotivoCategory[] }
> = {
  sem_interesse: { substage: SEM_INTERESSE_SUBSTAGE, categories: SEM_INTERESSE_CATEGORIES },
  pendente: { substage: PENDENTE_SUBSTAGE, categories: PENDENTE_CATEGORIES },
};

const labelMaps: Record<WorkspaceMotivoVariant, Map<string, string>> = {
  sem_interesse: new Map(),
  pendente: new Map(),
};
(Object.keys(WORKSPACE_MOTIVO_CONFIG) as WorkspaceMotivoVariant[]).forEach((variant) => {
  WORKSPACE_MOTIVO_CONFIG[variant].categories.forEach((cat) => {
    cat.options.forEach((opt) => labelMaps[variant].set(opt.key, opt.label));
  });
});

export function workspaceMotivoLabel(variant: WorkspaceMotivoVariant, key: string | null | undefined): string | null {
  if (!key) return null;
  return labelMaps[variant].get(key) || key;
}

export function isWorkspaceMotivoIncomplete(motivo?: WorkspaceMotivoData | null): boolean {
  const hasKeys = readWorkspaceMotivoKeys(motivo).length > 0;
  const hasFeedback = Boolean(motivo?.feedback?.trim());
  return !hasKeys && !hasFeedback;
}

export function getMotivoVariantForSubstage(substage: string): WorkspaceMotivoVariant | null {
  const normalized = normalizeWorkspaceSubstage(substage);
  if (SEM_INTERESSE_SUBSTAGES.has(normalized)) return 'sem_interesse';
  if (normalized === PENDENTE_SUBSTAGE) return 'pendente';
  return null;
}

export function isMotivoTabSubstage(substage: string): boolean {
  return getMotivoVariantForSubstage(substage) !== null;
}

/** Sub-sections that show incomplete-count warnings on accordion headers. */
export const MOTIVO_WARN_SUBSTAGES = new Set([
  SEM_INTERESSE_SUBSTAGE,
  PROSPECCAO_SEM_INTERESSE_SUBSTAGE,
  PENDENTE_SUBSTAGE,
]);
